import { useQuery } from '@tanstack/react-query'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { Metric, trendDelta } from '../../components/Metric.js'
import { Topbar } from '../../components/Topbar.js'
import { Donut } from '../../components/charts/Donut.js'
import { useAuth } from '../../lib/auth.js'
import { formatCount, formatRate } from '../../lib/format.js'
import { PERIOD_OPTIONS, usePeriod } from '../../lib/period.js'
import { usePageTitle } from '../../lib/page-title.js'
import { Button, Select, Text } from '../../ui/index.js'

interface Summary {
  readonly avgEfficiency: number
  readonly avgComplexity: number
  readonly onTimeRate: number
  readonly returnedForCorrection: number
}

interface ManagerRow {
  readonly userId: string
  readonly name: string
  readonly departmentId: string | null
  readonly teamSize: number
  readonly score: number | null
  readonly onTime: number | null
}

interface Attention {
  readonly overdue: number
  readonly blocked: number
  readonly active: number
  readonly total: number
}

interface TrendResponse {
  readonly points: readonly {
    readonly from: string
    readonly to: string
    readonly score: number | null
    readonly onTime: number | null
    readonly firstTimePass: number | null
  }[]
}

function managerSpread(managers: readonly ManagerRow[]) {
  // 8, 5, 4 => 3 managers with at least one direct report.
  return managers.filter((row) => row.teamSize > 0).length
}

export function CommandCentre() {
  usePageTitle('Command Centre')
  const { api } = useAuth()
  const { period, setPeriod, from, to, bucket } = usePeriod('quarter')

  const summaryQuery = useQuery({
    queryKey: ['command-summary', from, to],
    queryFn: () => api.get<Summary>(`/api/analytics/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  })

  const managersQuery = useQuery({
    queryKey: ['command-managers', from, to],
    queryFn: () => api.get<ManagerRow[]>(`/api/analytics/managers?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  })

  const attentionQuery = useQuery({
    queryKey: ['command-attention'],
    queryFn: () => api.get<Attention>('/api/analytics/attention'),
  })

  const trendQuery = useQuery({
    queryKey: ['command-trend', from, to, bucket],
    queryFn: () => api.get<TrendResponse>(`/api/analytics/trend?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&bucket=${bucket}`),
  })

  const loading = summaryQuery.isPending || managersQuery.isPending || attentionQuery.isPending || trendQuery.isPending
  const failure = summaryQuery.error ?? managersQuery.error ?? attentionQuery.error ?? trendQuery.error
  const hasFailure = summaryQuery.isError || managersQuery.isError || attentionQuery.isError || trendQuery.isError

  if (loading) {
    return (
      <>
        <Topbar title="Command centre" eyebrow="Super manager workspace" />
        <div className="page">
          <div className="page-section">
            <LoadingState label="Loading command centre" />
          </div>
        </div>
      </>
    )
  }

  if (hasFailure) {
    return (
      <>
        <Topbar title="Command centre" eyebrow="Super manager workspace" />
        <div className="page">
          <div className="page-section">
            <ErrorState error={failure} onRetry={() => void summaryQuery.refetch()} />
          </div>
        </div>
      </>
    )
  }

  const summary = summaryQuery.data!
  const managers = managersQuery.data ?? []
  const attention = attentionQuery.data!
  const trend = trendQuery.data?.points ?? []
  const spreadValue = managerSpread(managers)
  const efficiencyDelta = trendDelta(trend, (point) => point.score)
  const onTimeDelta = trendDelta(trend, (point) => point.onTime, 100)

  return (
    <>
      <Topbar
        title="Command centre"
        eyebrow="Super manager workspace"
        actions={
          <div style={{ display: 'flex', gap: 'var(--o-space-2)', alignItems: 'center' }}>
            <Select value={period} onChange={(value) => setPeriod(value as 'quarter' | 'year')}>
              {PERIOD_OPTIONS.filter((option) => option.value === 'quarter' || option.value === 'year').map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Button kind="secondary" size="small">
              Export
            </Button>
          </div>
        }
      />

      <div className="page">
        <div className="page-section" style={{ display: 'grid', gap: 'var(--o-space-4)' }}>
          <div className="metric-grid">
            <Metric label="Org efficiency" value={formatCount(summary.avgEfficiency)} unit="/100" delta={efficiencyDelta} trend={trend.map((point) => point.score)} />
            <Metric label="On-time rate" value={formatRate(summary.onTimeRate)} delta={onTimeDelta} deltaSuffix="pp" tone="pos" trend={trend.map((point) => point.onTime)} />
            <Metric label="Active tasks" value={formatCount(attention.active)} footnote="across the org" />
            <Metric label="Manager spread" value={formatCount(spreadValue)} unit="mgrs" tone="warn" footnote="managers with direct reports" />
          </div>

          <div className="split-grid split-grid--command">
            <section style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--o-space-3)' }}>
                <div>
                  <div className="card-label">Manager performance</div>
                  <h3 style={{ margin: 0 }}>Compare team outcomes across managers</h3>
                </div>
                <Text color="secondary">Null rows read “No closed tasks”.</Text>
              </div>
              <div style={{ display: 'grid', gap: 'var(--o-space-2)' }}>
                {managers.map((row) => (
                  <div
                    key={row.userId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                      gap: 'var(--o-space-3)',
                      alignItems: 'center',
                      padding: 'var(--o-space-3)',
                      borderRadius: 'var(--r-md)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-subtle)',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500 }}>{row.name}</div>
                      <Text color="secondary">Team size {row.teamSize}</Text>
                    </div>
                    <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-2)' }}>
                      {row.score === null ? 'No closed tasks' : `${formatCount(row.score)} /100`}
                    </div>
                    {/* No row sparkline: /analytics/managers has no time series, and a
                        flat three-point line fabricated one. The test asserts its absence. */}
                  </div>
                ))}
              </div>
            </section>

            <aside style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)' }}>
              <div className="card-label">Attention</div>
              <div style={{ display: 'grid', gap: 'var(--o-space-4)', marginTop: 'var(--o-space-3)' }}>
                <Donut
                  value={summary.avgEfficiency}
                  max={100}
                  size={104}
                  stroke={10}
                  label={
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 26, fontWeight: 600 }}>{formatCount(summary.avgEfficiency)}</div>
                      <div style={{ fontSize: 10, color: 'var(--fg-muted)' }}>EFFICIENCY</div>
                    </div>
                  }
                />
                <div style={{ display: 'grid', gap: 'var(--o-space-2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Overdue</span>
                    <strong>{formatCount(attention.overdue)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Blocked</span>
                    <strong>{formatCount(attention.blocked)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Total visible</span>
                    <strong>{formatCount(attention.total)}</strong>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  )
}
